<template>
  <section ref="sectionRef" class="flex flex-col gap-8 scroll-mt-24">
    <ClientOnly>
      <div
        v-if="combinedError"
        class="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-400 dark:bg-red-950/40 dark:text-red-200"
      >
        Unable to load testimonials right now. Please refresh and try again.
      </div>

      <div v-if="sectionLoading" class="flex flex-col gap-8">
        <div class="hidden xl:grid xl:grid-cols-[2fr_3fr] gap-8">
          <div
            class="h-full min-h-[320px] rounded-2xl bg-neutral-200 animate-pulse dark:bg-neutral-800"
          ></div>
          <div class="grid grid-cols-2 gap-6 auto-rows-fr" style="grid-template-rows: 1fr 1fr">
            <div
              v-for="n in 4"
              :key="`testimonial-skeleton-${n}`"
              class="h-full min-h-[160px] rounded-2xl bg-neutral-200 animate-pulse dark:bg-neutral-800"
            ></div>
          </div>
        </div>
        <div class="flex flex-col gap-8 xl:hidden">
          <div
            class="h-full min-h-[320px] rounded-2xl bg-neutral-200 animate-pulse dark:bg-neutral-800"
          ></div>
          <div
            class="grid grid-cols-1 sm:grid-cols-2 gap-6"
            style="grid-template-rows: 1fr 1fr 1fr 1fr"
          >
            <div
              v-for="n in 4"
              :key="`testimonial-skeleton-mobile-${n}`"
              class="h-full min-h-[160px] rounded-2xl bg-neutral-200 animate-pulse dark:bg-neutral-800"
            ></div>
          </div>
        </div>
      </div>

      <div v-else class="flex flex-col gap-8">
        <!-- Desktop Layout (xl and above) - Fixed -->
        <div class="hidden xl:grid xl:grid-cols-[2fr_3fr] gap-8">
          <!-- Left Column - Video Testimonial -->
          <div class="testimonial-primary-slot w-full h-full" style="aspect-ratio: auto">
            <div v-if="featuredVideo" class="w-full h-full">
              <ContentCard :content="featuredVideo" />
            </div>
            <div
              v-else
              class="flex items-center justify-center rounded-2xl border border-dashed border-neutral-300 p-8 text-center text-neutral-500 dark:border-neutral-700 dark:text-neutral-400 h-full"
            >
              <p>No video testimonials published yet.</p>
            </div>
          </div>

          <!-- Right Column - Text Testimonials Grid -->
          <div class="grid grid-cols-2 gap-6 auto-rows-fr" :style="gridTemplateRows">
            <template
              v-for="(textTestimonial, index) in paginatedTextTestimonials"
              :key="`desktop-${index}`"
            >
              <div
                class="testimonial-secondary-slot w-full h-full"
                :style="getGridItemStyle(index)"
              >
                <ContentCard :content="textTestimonial" />
              </div>
            </template>
            <!-- Show empty state if no testimonials -->
            <div
              v-if="paginatedTextTestimonials.length === 0"
              class="flex items-center justify-center rounded-2xl border border-dashed border-neutral-300 p-4 text-center text-neutral-500 dark:border-neutral-700 dark:text-neutral-400 text-sm h-full col-span-2"
            >
              <p>No testimonials available yet.</p>
            </div>
          </div>
        </div>

        <!-- Mobile/Tablet Layout (below xl) -->
        <div class="flex flex-col gap-8 xl:hidden">
          <!-- Video Testimonial - Full width -->
          <div v-if="featuredVideo" class="testimonial-primary-slot w-full">
            <ContentCard :content="featuredVideo" />
          </div>
          <div
            v-else
            class="flex items-center justify-center rounded-2xl border border-dashed border-neutral-300 p-8 text-center text-neutral-500 dark:border-neutral-700 dark:text-neutral-400 aspect-[4/3]"
          >
            <p>No video testimonials published yet.</p>
          </div>

          <!-- Text Testimonials Grid - 1 column on mobile/tablet -->
          <div class="grid grid-cols-1 gap-6" :style="mobileGridTemplateRows">
            <template
              v-for="(textTestimonial, index) in paginatedTextTestimonials"
              :key="`mobile-${index}`"
            >
              <div class="testimonial-secondary-slot w-full" :style="getMobileGridItemStyle(index)">
                <ContentCard :content="textTestimonial" />
              </div>
            </template>
            <!-- Show empty state if no testimonials -->
            <div
              v-if="paginatedTextTestimonials.length === 0"
              class="flex items-center justify-center rounded-2xl border border-dashed border-neutral-300 p-4 text-center text-neutral-500 dark:border-neutral-700 dark:text-neutral-400 text-sm aspect-[4/3] col-span-1"
            >
              <p>No testimonials available yet.</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination -->
      <div v-if="!sectionLoading && totalPages > 1" class="flex justify-end mt-4">
        <UPagination v-model="currentPage" :total="totalPages" :page-count="1" :max="7" size="lg" />
      </div>

      <template #fallback>
        <div class="flex flex-col gap-8">
          <div class="hidden xl:grid xl:grid-cols-[2fr_3fr] gap-8">
            <div
              class="h-full min-h-[320px] rounded-2xl bg-neutral-200 animate-pulse dark:bg-neutral-800"
            ></div>
            <div class="grid grid-cols-2 gap-6 auto-rows-fr" style="grid-template-rows: 1fr 1fr">
              <div
                v-for="n in 4"
                :key="`testimonial-skeleton-${n}`"
                class="h-full min-h-[160px] rounded-2xl bg-neutral-200 animate-pulse dark:bg-neutral-800"
              ></div>
            </div>
          </div>
          <div class="flex flex-col gap-8 xl:hidden">
            <div
              class="h-full min-h-[320px] rounded-2xl bg-neutral-200 animate-pulse dark:bg-neutral-800"
            ></div>
            <div
              class="grid grid-cols-1 sm:grid-cols-2 gap-6"
              style="grid-template-rows: 1fr 1fr 1fr 1fr"
            >
              <div
                v-for="n in 4"
                :key="`testimonial-skeleton-mobile-${n}`"
                class="h-full min-h-[160px] rounded-2xl bg-neutral-200 animate-pulse dark:bg-neutral-800"
              ></div>
            </div>
          </div>
        </div>
      </template>
    </ClientOnly>
  </section>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { useTestimonials } from '~/composables/useTestimonials';
import type { Testimonial } from '~/composables/useTestimonials';
import ContentCard from '~/components/content-card/ContentCard.vue';

type VideoTestimonial = Extract<Testimonial, { mediaType: 'hls' }>;
type TextTestimonial = Extract<Testimonial, { mediaType: 'text' }>;

const sectionRef = ref<HTMLElement | null>(null);
const currentPage = ref(1);
const itemsPerPage = 4;

// Scroll to section when page changes
watch(currentPage, () => {
  if (sectionRef.value) {
    sectionRef.value.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
});

const {
  testimonials,
  isLoading: testimonialsLoading,
  error: testimonialsError,
} = useTestimonials();

const toTimestamp = (input?: string) => {
  if (!input) return 0;
  const timestamp = new Date(input).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const allVideoTestimonials = computed(() => {
  const all = testimonials.value ?? [];
  const videos = all.filter((item) => item.mediaType === 'hls') as VideoTestimonial[];
  return videos.sort((a, b) => toTimestamp(b.createdAt) - toTimestamp(a.createdAt));
});

const allTextTestimonials = computed(() => {
  const all = testimonials.value ?? [];
  const texts = all.filter((item) => item.mediaType === 'text') as TextTestimonial[];
  return texts.sort((a, b) => toTimestamp(b.createdAt) - toTimestamp(a.createdAt));
});

// Calculate which video to show for current page
const currentVideoIndex_Page = computed(() => {
  return Math.min(currentPage.value - 1, allVideoTestimonials.value.length - 1);
});

const featuredVideo = computed(() => {
  if (currentVideoIndex_Page.value < 0) return null;
  return allVideoTestimonials.value[currentVideoIndex_Page.value] ?? null;
});

// Calculate text testimonials for current page
const paginatedTextTestimonials = computed(() => {
  const startIndex = (currentPage.value - 1) * itemsPerPage;
  return allTextTestimonials.value.slice(startIndex, startIndex + itemsPerPage);
});

const textGridItems = computed<(TextTestimonial | null)[]>(() => {
  const items: (TextTestimonial | null)[] = [...paginatedTextTestimonials.value];
  // Only pad to 4 if we have 3 items (to maintain 2x2 grid for 3 items)
  if (items.length === 3) {
    items.push(null);
  }
  return items;
});

// Calculate grid template rows based on available testimonials
const gridTemplateRows = computed(() => {
  const availableCount = paginatedTextTestimonials.value.length;
  if (availableCount === 0) return '1fr';
  if (availableCount === 1) return '1fr';
  if (availableCount === 2) return '1fr'; // Only 1 row needed for 2 side-by-side items
  if (availableCount === 3) return '1fr 1fr';
  return '1fr 1fr'; // 4+ items
});

// Get grid item style for flexible positioning
const getGridItemStyle = (index: number) => {
  const availableCount = paginatedTextTestimonials.value.length;

  // If we have 1 testimonial, center it
  if (availableCount === 1) {
    return {
      gridColumn: '1 / -1',
      gridRow: '1 / 2',
    };
  }

  // If we have 2 testimonials, place them side by side
  if (availableCount === 2) {
    return {
      gridColumn: `${index + 1} / ${index + 2}`,
      gridRow: '1 / 2',
    };
  }

  // If we have 3 testimonials, place first one spanning full width, then 2 side by side
  if (availableCount === 3) {
    if (index === 0) {
      return {
        gridColumn: '1 / -1',
        gridRow: '1 / 2',
      };
    } else {
      return {
        gridColumn: `${index} / ${index + 1}`,
        gridRow: '2 / 3',
      };
    }
  }

  // Default 2x2 grid for 4+ testimonials
  return {
    gridColumn: `${(index % 2) + 1} / ${(index % 2) + 2}`,
    gridRow: `${Math.floor(index / 2) + 1} / ${Math.floor(index / 2) + 2}`,
  };
};

// Mobile grid template rows (single column layout)
const mobileGridTemplateRows = computed(() => {
  const availableCount = paginatedTextTestimonials.value.length;
  if (availableCount === 0) return '1fr';
  if (availableCount === 1) return '1fr';
  if (availableCount === 2) return '1fr 1fr';
  if (availableCount === 3) return '1fr 1fr 1fr';
  return '1fr 1fr 1fr 1fr'; // 4+ items
});

// Get mobile grid item style for single column layout
const getMobileGridItemStyle = (index: number) => {
  return {
    gridColumn: '1 / -1',
    gridRow: `${index + 1} / ${index + 2}`,
  };
};

// Calculate total pages for pagination (max of videos or text pages)
const totalPages = computed(() => {
  const videoCount = allVideoTestimonials.value.length;
  const textCount = allTextTestimonials.value.length;
  const textPages = Math.ceil(textCount / itemsPerPage);
  return Math.max(videoCount, textPages);
});

const sectionLoading = computed(() => testimonialsLoading.value || !testimonials.value);

const combinedError = computed(() => testimonialsError.value || null);
</script>

<style scoped>
/* Desktop (xl+): Override default aspect ratio for grid layout */
@media (min-width: 1280px) {
  .testimonial-primary-slot :deep(.content-card),
  .testimonial-secondary-slot :deep(.content-card),
  .testimonial-secondary-slot :deep(article) {
    aspect-ratio: auto !important;
    height: 100% !important;
    width: 100% !important;
    min-height: 24rem !important;
  }
}

/* Mobile/Tablet (below xl): all cards use 4:3 aspect ratio */
@media (max-width: 1279px) {
  .testimonial-primary-slot :deep(.content-card),
  .testimonial-primary-slot :deep(article),
  .testimonial-secondary-slot :deep(.content-card),
  .testimonial-secondary-slot :deep(article) {
    aspect-ratio: 4/3 !important;
    height: auto !important;
    width: 100% !important;
    min-height: 24rem !important;
  }
}

/* Primary slot - larger gap and padding */
.testimonial-primary-slot :deep(.text-overlay) {
  gap: 1rem !important;
  padding: 2.5rem 2rem !important; /* py-10 px-8 */
}

/* Testimonial secondary slot - consistent spacing and smaller text */
.testimonial-secondary-slot :deep(.text-overlay) {
  gap: 1rem !important;
}

.testimonial-secondary-slot :deep(.card-title h3) {
  font-size: clamp(16px, 4.2vw, 21px) !important;
}

.testimonial-secondary-slot :deep(.card-description-text) {
  font-size: clamp(12px, 3.6vw, 14.4px) !important;
}
</style>
